<template>
  <section :class="['trademark-section', { 'is-embed': embedMode }]">
    <div :class="{ 'core-container': !embedMode }">
      <div :class="['trademark-panel', { 'is-embed': embedMode }]">
        <div class="trademark-panel__toolbar">
          <h2 class="trademark-panel__title">{{ title }}</h2>

          <div
            v-if="toolbarMode === 'full'"
            class="trademark-panel__filters"
          >
            <select
              v-model="filters.category"
              class="trademark-panel__select"
              @change="reloadList"
            >
              <option
                v-for="opt in filterOptions.categories"
                :key="opt.value || 'all-cat'"
                :value="opt.value"
              >{{ opt.label }}</option>
            </select>
            <select
              v-model="filters.status"
              class="trademark-panel__select"
              @change="reloadList"
            >
              <option
                v-for="opt in filterOptions.statuses"
                :key="opt.value || 'all-status'"
                :value="opt.value"
              >{{ opt.label }}</option>
            </select>
            <select
              v-model="filters.price"
              class="trademark-panel__select"
              @change="reloadList"
            >
              <option
                v-for="opt in filterOptions.prices"
                :key="opt.value || 'all-price'"
                :value="opt.value"
              >{{ opt.label }}</option>
            </select>
            <select
              v-model="filters.sort"
              class="trademark-panel__select"
              @change="reloadList"
            >
              <option
                v-for="opt in filterOptions.sorts"
                :key="opt.value"
                :value="opt.value"
              >{{ opt.label }}</option>
            </select>
          </div>

          <div class="trademark-panel__sort">
            <button
              v-for="tab in sortTabs"
              :key="tab.key"
              type="button"
              :class="[
                'trademark-panel__sort-btn',
                { 'is-active': activeSort === tab.key }
              ]"
              @click="changeSortTab(tab.key)"
            >{{ tab.label }}</button>
          </div>
        </div>

        <div v-if="loading" class="trademark-panel__state">
          <div class="trademark-panel__spinner" />
          <p>正在加载商标数据...</p>
        </div>

        <div v-else-if="error" class="trademark-panel__state is-error">
          <p>{{ error }}</p>
          <button type="button" class="trademark-panel__retry" @click="loadList">
            重新加载
          </button>
        </div>

        <div v-else class="trademark-panel__grid">
          <a
            v-for="item in list"
            :key="item.id"
            :href="detailHref(item.id)"
            class="trademark-card"
          >
            <div class="trademark-card__top">
              <div class="trademark-card__badges">
                <span v-if="item.is_new" class="trademark-card__badge is-new">新</span>
                <span v-if="item.is_hot" class="trademark-card__badge is-hot">热</span>
              </div>

              <div
                class="trademark-card__image"
                :style="getCardStyle(item)"
              >
                <img
                  v-if="item.cover_img"
                  :src="item.cover_img"
                  :alt="item.name"
                  class="trademark-card__img"
                />
                <template v-else>
                  <span class="trademark-card__name-lg">{{ item.name }}</span>
                </template>
              </div>
            </div>

            <div class="trademark-card__info">
              <p class="trademark-card__category">
                【{{ formatCategory(item.category_id) }}类】 -{{ item.name }}
              </p>
              <div class="trademark-card__meta">
                <p class="trademark-card__price">
                  <template v-if="item.price > 0">
                    <span class="trademark-card__price-symbol">¥</span>
                    <span class="trademark-card__price-value">{{ formatPrice(item.price) }}</span>
                  </template>
                  <span v-else class="trademark-card__price-negotiable">面议</span>
                </p>
                <p class="trademark-card__items">{{ item.items_count }}小项</p>
              </div>
            </div>
          </a>
        </div>

        <div v-if="!loading && !error" class="trademark-panel__footer">
          <p class="trademark-panel__summary">
            共 {{ pagination.total }} 条，每页 {{ pagination.pageSize }} 条
          </p>
          <div class="trademark-panel__pagination">
            <button
              type="button"
              class="trademark-panel__page-btn"
              :disabled="pagination.page <= 1"
              @click="goPage(pagination.page - 1)"
            >上一页</button>
            <button
              v-for="p in displayPages"
              :key="p.key"
              type="button"
              :class="[
                'trademark-panel__page-btn',
                {
                  'is-active': p.num === pagination.page,
                  'is-ellipsis': p.ellipsis
                }
              ]"
              :disabled="p.ellipsis"
              @click="!p.ellipsis && goPage(p.num)"
            >{{ p.label }}</button>
            <button
              type="button"
              class="trademark-panel__page-btn"
              :disabled="pagination.page >= pagination.totalPages"
              @click="goPage(pagination.page + 1)"
            >下一页</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { fetchTrademarkList } from '@/api/trademark'
import {
  FILTER_OPTIONS,
  SORT_TABS,
  TRADEMARK_PAGE_SIZE
} from '@/constants/trademark'
import { SEARCH_FILTER_OPTIONS } from '@/constants/searchPage'
import { detailHref as buildDetailHref } from '@/utils/pageNavigation'

const MAX_PAGE_BUTTONS = 5

export default {
  name: 'TrademarkGrid',
  props: {
    title: {
      type: String,
      default: '精品商标推荐'
    },
    pageSize: {
      type: Number,
      default: TRADEMARK_PAGE_SIZE
    },
    filterOptionsSource: {
      type: String,
      default: 'home',
      validator: value => ['home', 'search'].includes(value)
    },
    autoLoad: {
      type: Boolean,
      default: true
    },
    embedMode: {
      type: Boolean,
      default: false
    },
    toolbarMode: {
      type: String,
      default: 'full',
      validator: value => ['full', 'sort-only'].includes(value)
    }
  },
  data() {
    return {
      list: [],
      loading: false,
      error: null,
      sortTabs: SORT_TABS,
      activeSort: 'default',
      pagination: {
        page: 1,
        pageSize: this.pageSize,
        total: 0,
        totalPages: 0
      },
      filters: {
        category: '',
        group: '',
        sameNameMulti: false,
        status: '',
        price: '',
        priceMin: '',
        priceMax: '',
        sort: 'default'
      },
      searchKeyword: '',
      searchType: 1
    }
  },
  computed: {
    filterOptions() {
      return this.filterOptionsSource === 'search'
        ? SEARCH_FILTER_OPTIONS
        : FILTER_OPTIONS
    },
    displayPages() {
      const total = this.pagination.totalPages
      const current = this.pagination.page
      const pages = []

      if (total <= MAX_PAGE_BUTTONS + 2) {
        for (let i = 1; i <= total; i++) {
          pages.push({ key: `p-${i}`, num: i, label: String(i) })
        }
        return pages
      }

      pages.push({ key: 'p-1', num: 1, label: '1' })

      let start = Math.max(2, current - 1)
      let end = Math.min(total - 1, current + 1)

      if (current <= 3) {
        start = 2
        end = MAX_PAGE_BUTTONS
      } else if (current >= total - 2) {
        start = total - MAX_PAGE_BUTTONS + 1
        end = total - 1
      }

      if (start > 2) {
        pages.push({ key: 'ellipsis-start', ellipsis: true, label: '...' })
      }

      for (let i = start; i <= end; i++) {
        pages.push({ key: `p-${i}`, num: i, label: String(i) })
      }

      if (end < total - 1) {
        pages.push({ key: 'ellipsis-end', ellipsis: true, label: '...' })
      }

      pages.push({ key: `p-${total}`, num: total, label: String(total) })
      return pages
    }
  },
  created() {
    if (this.autoLoad) {
      this.loadList()
    }
  },
  methods: {
    loadList() {
      this.loading = true
      this.error = null

      const params = this.buildQueryParams()
      fetchTrademarkList(params)
        .then(result => {
          if (result.status !== 1 || !result.data) {
            this.error = result.desc || result.message || '加载数据失败'
            this.list = []
            return
          }

          this.list = result.data.list || []
          this.pagination = result.data.pagination || {
            page: 1,
            pageSize: TRADEMARK_PAGE_SIZE,
            total: 0,
            totalPages: 0
          }
        })
        .catch(() => {
          this.error = '网络错误，请稍后重试'
          this.list = []
        })
        .finally(() => {
          this.loading = false
        })
    },
    buildQueryParams() {
      const params = {
        page: this.pagination.page,
        pageSize: this.pageSize,
        sort: this.resolveSort()
      }

      if (this.filters.category) {
        params.category = this.filters.category
      }
      if (this.filters.group) {
        params.group = this.filters.group
      }
      if (this.filters.sameNameMulti) {
        params.same_name_flag = 1
      }
      if (this.filters.status && this.filters.status !== 'on_sale') {
        params.status = this.filters.status
      }

      if (this.filters.priceMin) {
        params.price_min = Number(this.filters.priceMin)
      } else {
        const priceOpt = this.filterOptions.prices.find(
          item => item.value === this.filters.price
        )
        if (priceOpt && priceOpt.min > 0) {
          params.price_min = priceOpt.min
        }
      }

      if (this.filters.priceMax) {
        params.price_max = Number(this.filters.priceMax)
      } else {
        const priceOpt = this.filterOptions.prices.find(
          item => item.value === this.filters.price
        )
        if (priceOpt && priceOpt.max > 0) {
          params.price_max = priceOpt.max
        }
      }

      const keyword = this.searchKeyword.trim()
      if (keyword) {
        params.keyword = keyword
        params.search_type = this.searchType
      }

      return params
    },
    setFilters({
      keyword = '',
      searchType = 1,
      category = '',
      group = '',
      sameNameMulti = false,
      status = '',
      price = '',
      priceMin = '',
      priceMax = '',
      sort = 'default',
      activeSort = 'default'
    } = {}) {
      this.searchKeyword = keyword
      this.searchType = searchType
      this.filters.category = category ? String(category) : ''
      this.filters.group = group ? String(group) : ''
      this.filters.sameNameMulti = Boolean(sameNameMulti)
      this.filters.status = status || ''
      this.filters.price = price || ''
      this.filters.priceMin = priceMin ? String(priceMin) : ''
      this.filters.priceMax = priceMax ? String(priceMax) : ''
      this.filters.sort = sort || 'default'
      this.activeSort = activeSort || 'default'
    },
    resolveSort() {
      if (this.filters.sort !== 'default') {
        return this.filters.sort
      }
      const tab = this.sortTabs.find(item => item.key === this.activeSort)
      return tab ? tab.sort : 'default'
    },
    getFiltersState() {
      return {
        keyword: this.searchKeyword,
        searchType: this.searchType,
        category: this.filters.category,
        group: this.filters.group,
        sameNameMulti: this.filters.sameNameMulti,
        status: this.filters.status,
        price: this.filters.price,
        priceMin: this.filters.priceMin,
        priceMax: this.filters.priceMax,
        sort: this.filters.sort,
        activeSort: this.activeSort
      }
    },
    emitFiltersChange() {
      this.$emit('filters-change', this.getFiltersState())
    },
    reloadList() {
      this.pagination.page = 1
      this.loadList()
      this.emitFiltersChange()
    },
    applySearch({
      keyword = '',
      category = '',
      group = '',
      sameNameMulti = false,
      searchType = 1,
      status = '',
      price = '',
      priceMin = '',
      priceMax = '',
      sort = 'default',
      activeSort = 'default'
    } = {}) {
      this.setFilters({
        keyword,
        searchType,
        category,
        group,
        sameNameMulti,
        status,
        price,
        priceMin,
        priceMax,
        sort,
        activeSort
      })
      this.reloadList()
    },
    scrollIntoView() {
      this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    changeSortTab(key) {
      this.activeSort = key
      this.filters.sort = 'default'
      this.reloadList()
    },
    goPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return
      this.pagination.page = page
      this.loadList()
    },
    formatCategory(id) {
      return String(id).padStart(2, '0')
    },
    formatPrice(price) {
      if (price >= 10000) {
        return (price / 10000).toFixed(2) + '万'
      }
      return Math.round(price).toLocaleString('zh-CN')
    },
    getCardStyle(item) {
      if (item.cover_img) {
        return { background: '#ffffff' }
      }
      return { background: item.color || '#3498db' }
    },
    detailHref(id) {
      return buildDetailHref(id)
    }
  }
}
</script>

<style scoped>
.trademark-section {
  padding: var(--space-lg) 0 var(--space-xl);
}

.trademark-section.is-embed {
  padding: 0;
}

.trademark-panel {
  background: var(--color-bg-page);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  padding: var(--space-lg) var(--space-xl) var(--space-xl);
}

.trademark-panel.is-embed {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  padding: var(--search-panel-padding-y) var(--search-panel-padding-x) var(--space-xl);
}

.trademark-panel__toolbar {
  display: flex;
  align-items: center;
  gap: var(--space-lg);
  padding-bottom: var(--space-lg);
  border-bottom: 1px solid var(--color-border-light);
  margin-bottom: var(--space-lg);
}

.trademark-panel__title {
  flex-shrink: 0;
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--color-text-primary);
}

.trademark-panel__filters {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-sm);
  min-width: 0;
}

.trademark-panel__select {
  width: 100%;
  height: 36px;
  padding: 0 var(--space-xl) 0 var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  background: var(--color-bg-page)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")
    no-repeat right var(--space-sm) center;
  appearance: none;
  outline: none;
  cursor: pointer;
}

.trademark-panel__select:focus {
  border-color: var(--color-primary);
}

.trademark-panel__sort {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.trademark-panel__sort-btn {
  position: relative;
  padding: var(--space-sm) var(--space-md);
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  border-radius: var(--radius-pill);
  transition: all 0.2s;
}

.trademark-panel__sort-btn + .trademark-panel__sort-btn {
  margin-left: var(--space-sm);
  padding-left: var(--space-md);
  border-left: 1px solid var(--color-border);
}

.trademark-panel__sort-btn.is-active {
  background: var(--color-primary);
  color: var(--color-text-inverse);
  border-left-color: transparent;
}

.trademark-panel__sort-btn.is-active + .trademark-panel__sort-btn {
  border-left-color: var(--color-border);
}

.trademark-panel__state {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  font-size: var(--font-size-md);
}

.trademark-panel__state.is-error {
  color: #dc2626;
}

.trademark-panel__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: var(--space-md);
}

.trademark-panel__retry {
  margin-top: var(--space-md);
  padding: var(--space-sm) var(--space-lg);
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: var(--font-size-sm);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.trademark-panel__grid {
  display: grid;
  grid-template-columns: repeat(var(--grid-columns-trademark), 1fr);
  gap: var(--grid-gap-trademark);
}

.trademark-card {
  position: relative;
  display: block;
  color: inherit;
  text-decoration: none;
  background: var(--color-bg-page);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  box-shadow: var(--shadow-card);
}

.trademark-card:hover {
  transform: translateY(-2px);
  border-color: #d0d7de;
  box-shadow: var(--shadow-card-hover);
}

.trademark-card__top {
  position: relative;
  padding: var(--card-top-padding) var(--card-info-padding-x) var(--space-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(var(--card-image-size) + var(--card-top-padding) * 2);
}

.trademark-card__badges {
  position: absolute;
  top: var(--space-sm);
  left: var(--space-sm);
  display: flex;
  gap: var(--space-xs);
  z-index: 2;
}

.trademark-card__badge {
  min-width: 20px;
  height: 20px;
  padding: 0 var(--space-xs);
  border-radius: var(--radius-pill);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: var(--color-text-inverse);
}

.trademark-card__badge.is-new { background: #4cd964; }
.trademark-card__badge.is-hot { background: #ff9500; }

.trademark-card__image {
  width: var(--card-image-size);
  height: var(--card-image-size);
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.trademark-card__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.25s ease;
}

.trademark-card:hover .trademark-card__img {
  transform: scale(1.08);
}

.trademark-card__name-lg {
  font-size: var(--card-fallback-name-size);
  font-weight: 700;
  color: var(--color-text-inverse);
  padding: 0 var(--space-xs);
  text-align: center;
  line-height: 1.2;
  word-break: break-all;
}

.trademark-card__info {
  padding: var(--card-info-padding-y) var(--card-info-padding-x) var(--card-info-padding-bottom);
  border-top: 1px solid var(--color-border-light);
  text-align: center;
}

.trademark-card__category {
  font-size: 15px;
  color: #4a4a4a;
  font-weight: 300;
  letter-spacing: 1px;
  margin-bottom: var(--space-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trademark-card__meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-sm);
}

.trademark-card__price {
  display: flex;
  align-items: baseline;
  gap: 2px;
  color: var(--color-price);
  font-weight: 700;
}

.trademark-card__price-symbol {
  font-size: var(--font-size-xs);
}

.trademark-card__price-value {
  font-size: var(--font-size-lg);
}

.trademark-card__price-negotiable {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  font-weight: 400;
}

.trademark-card__items {
  font-size: var(--font-size-sm);
  color: #a0a0a0;
  font-weight: 300;
  flex-shrink: 0;
}

.trademark-panel__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: var(--space-lg);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-border-light);
}

.trademark-panel__summary {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.trademark-panel__pagination {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
}

.trademark-panel__page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-page);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  transition: all 0.2s;
}

.trademark-panel__page-btn:hover:not(:disabled):not(.is-ellipsis) {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.trademark-panel__page-btn.is-active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-text-inverse);
}

.trademark-panel__page-btn.is-ellipsis {
  border-color: transparent;
  background: transparent;
  cursor: default;
}

.trademark-panel__page-btn:disabled:not(.is-ellipsis) {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
